/**
 * @fileoverview Shared creation-method picker modal for the document template builder.
 * @module ui-admin/components/NewDocumentEntryModal
 *
 * Responsibilities: - Present three document-creation methods (Scratch, Library, AI) as large
 *                     clickable cards so users can choose how they want to start a new template.
 * Dependencies:     - React, lucide-react icons.
 * Architecture:     - Pure presentational modal; no data access. Caller handles navigation.
 * Notes:            - Used by both tenant-web (DocumentTemplatesPage) and system-web
 *                     (SystemDocumentsPage). The "library" card label/description is customisable
 *                     so each app can describe its own library concept.
 *
 * @component NewDocumentEntryModal
 * Purpose:     Modal dialog that lets the user pick a document-creation method before entering
 *              the canvas builder.
 * Props:       open, onClose, onSelect, libraryLabel?, libraryDescription?
 * Behavior:    Renders three method cards; clicking any card calls onSelect then closes.
 * Data Source: none
 */
import React from 'react';
import { X, PenLine, BookOpen, Sparkles } from 'lucide-react';

export type DocumentCreationMethod = 'scratch' | 'library' | 'ai';

export interface NewDocumentEntryModalProps {
  open: boolean;
  onClose: () => void;
  /** Called when the user selects a creation method. */
  onSelect: (method: DocumentCreationMethod) => void;
  /** Label for the "library" card. Defaults to "From Library". */
  libraryLabel?: string;
  /** Description for the "library" card. */
  libraryDescription?: string;
}

interface MethodCard {
  method: DocumentCreationMethod;
  icon: React.ReactNode;
  title: string;
  description: string;
  accent: string;
  iconBg: string;
  border: string;
  hoverBorder: string;
}

/**
 * @component NewDocumentEntryModal
 */
export default function NewDocumentEntryModal({
  open,
  onClose,
  onSelect,
  libraryLabel = 'From Library',
  libraryDescription = 'Adopt a ready-made template from the system library and customise it for your department.',
}: NewDocumentEntryModalProps) {
  if (!open) return null;

  const cards: MethodCard[] = [
    {
      method: 'scratch',
      icon: <PenLine className="h-8 w-8" />,
      title: 'Start from Scratch',
      description: 'Build your document on a blank canvas with the full layout editor — fonts, borders, dynamic data, and more.',
      accent: 'text-orange-600',
      iconBg: 'bg-orange-50',
      border: 'border-gray-200',
      hoverBorder: 'hover:border-orange-400 hover:shadow-orange-100',
    },
    {
      method: 'library',
      icon: <BookOpen className="h-8 w-8" />,
      title: libraryLabel,
      description: libraryDescription,
      accent: 'text-blue-600',
      iconBg: 'bg-blue-50',
      border: 'border-gray-200',
      hoverBorder: 'hover:border-blue-400 hover:shadow-blue-100',
    },
    {
      method: 'ai',
      icon: <Sparkles className="h-8 w-8" />,
      title: 'With AI',
      description: 'Upload an existing PDF, Word doc, or Markdown file and let AI recreate it as an editable template — instantly.',
      accent: 'text-purple-600',
      iconBg: 'bg-purple-50',
      border: 'border-gray-200',
      hoverBorder: 'hover:border-purple-400 hover:shadow-purple-100',
    },
  ];

  const handleSelect = (method: DocumentCreationMethod) => {
    onSelect(method);
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="new-doc-modal-title"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-xs"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Panel */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden">
        {/* Header */}
        <div className="px-8 pt-8 pb-4">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
          <h2 id="new-doc-modal-title" className="text-xl font-semibold text-gray-900">
            Create New Template
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            How would you like to start?
          </p>
        </div>

        {/* Cards */}
        <div className="px-8 pb-8 flex flex-col gap-3">
          {cards.map((card) => (
            <button
              key={card.method}
              onClick={() => handleSelect(card.method)}
              className={`
                group flex items-start gap-4 w-full text-left
                rounded-xl border-2 ${card.border} ${card.hoverBorder}
                p-5 transition-all duration-150
                hover:shadow-md focus:outline-hidden focus-visible:ring-2 focus-visible:ring-orange-400
              `}
            >
              <span className={`mt-0.5 shrink-0 rounded-xl p-2.5 ${card.iconBg} ${card.accent} transition-colors`}>
                {card.icon}
              </span>
              <span className="flex flex-col min-w-0">
                <span className={`text-base font-semibold text-gray-900 group-hover:${card.accent} transition-colors`}>
                  {card.title}
                </span>
                <span className="mt-1 text-sm text-gray-500 leading-relaxed">
                  {card.description}
                </span>
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
